/**
 * De JSON aninhado para os nós que a árvore desenha — função pura.
 *
 * Separado do renderer porque é onde os erros acontecem, e todos em silêncio:
 * um `childrenPath` errado deixa a árvore rasa, um `labelPath` errado enche a
 * tela de `[object Object]`, e um dado com ciclo trava o navegador. Nenhum
 * desses dá erro; todos produzem uma tela errada.
 */
import { NzTreeNodeOptions } from 'ng-zorro-antd/tree';

/** Onde cada coisa está dentro do item. */
export interface TreeMapping {
  readonly childrenPath: string;
  readonly labelPath: string;
  readonly idPath?: string;
  /** Níveis abertos ao desenhar. 0 = só a raiz; -1 = todos. */
  readonly expandToLevel?: number;
}

/**
 * Profundidade máxima.
 *
 * Dado com ciclo — um filho que aponta para o pai — faria a recursão não parar
 * nunca, e o navegador trava sem uma mensagem sequer. Vinte níveis é mais do que
 * qualquer categoria de marketplace real, e é um limite que se percebe (a árvore
 * fica truncada) em vez de um que derruba.
 */
export const MAX_TREE_DEPTH = 20;

export function toTreeNodes(
  data: unknown,
  mapping: TreeMapping
): NzTreeNodeOptions[] {

  const items = Array.isArray(data) ? data : (data === null || data === undefined ? [] : [data]);

  return items.map((item, index) => convert(item, mapping, 0, String(index)));
}

function convert(
  item: unknown,
  mapping: TreeMapping,
  depth: number,
  fallbackKey: string
): NzTreeNodeOptions {

  const label = readPath(item, mapping.labelPath);
  const id = mapping.idPath === undefined ? undefined : readPath(item, mapping.idPath);
  const children = readPath(item, mapping.childrenPath);

  const key = id === undefined || id === null ? fallbackKey : String(id);

  // `title` sempre TEXTO: `[object Object]` numa árvore inteira é o resultado
  // de um `labelPath` que aponta para um objeto, e é o defeito mais comum aqui.
  const title = label === null || label === undefined
    ? ''
    : (typeof label === 'object' ? '' : String(label));

  const filhos = depth >= MAX_TREE_DEPTH || !Array.isArray(children)
    ? []
    : children.map(
      (child, index) => convert(child, mapping, depth + 1, key + '.' + index)
    );

  const expandido = mapping.expandToLevel === -1
    || depth < (mapping.expandToLevel ?? 0);

  return {
    key,
    title,
    expanded: expandido && filhos.length > 0,
    isLeaf: filhos.length === 0,
    children: filhos
  };
}

/** Lê um caminho com ponto. Devolve `undefined` quando o caminho não existe. */
function readPath(source: unknown, path: string): unknown {
  if (path.length === 0) {
    return undefined;
  }

  let current = source;

  for (const segment of path.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}
